import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonImg,
  IonLabel,
  IonPage,
  IonSpinner,
  isPlatform,
} from '@ionic/react';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import AppImg from '@/assets/imgs/login-app-ads2.webp';
import PasswordErrorImg from '@/assets/imgs/password-error.svg';
import LogoPepper from '@/assets/imgs/pepper-logo.svg';
import LoginScreen from '@/components/auth/LoginScreen/LoginScreen';
import NewPasswordHint from '@/components/auth/NewPasswordHint/NewPasswordHint';
import PinInputs, { PinInputsHandle } from '@/components/auth/PinInputs/PinInputs';
import SetupBiometryModal from '@/components/auth/SetupBiometryModal';
import { SetupBiometryModalProps } from '@/components/auth/SetupBiometryModal/SetupBiometryModal';
import NotificationModal from '@/components/common/NotificationModal';
import SuccessModal from '@/components/common/SucessModal';
import { ROUTES_PATH } from '@/constants';
import ENVIRONMENT from '@/environments/environment';
import useModal from '@/hooks/useModal';
import useNewPassword from '@/hooks/useNewPassword';
import { ClientTaxonomyEvents } from '@/interfaces/AnalyticsInterface/AnalyticsInterface';
import {
  checkIsLogged,
  clearExpiredCredentials,
  ExpiredCredentials,
  selectExpiredCredentials,
  selectPreviousPassword,
} from '@/redux/auth/auth';
import {
  changePassword, postLeads, postPasswordChange, postToken,
} from '@/redux/auth/authAPI';
import { addError } from '@/redux/error/error';
import { Analytics } from '@/services/analytics';
import {
  clearBiometricCredentials,
  setBiometricCredentials,
  typeOfBiometryAvailable,
} from '@/services/biometry';
import InsightService from '@/services/insights';
import { storageService } from '@/services/storage';
import { loginTrack } from '@/services/tracking';

import LoginAd from '../LoginAd/LoginAd';
import styles from './ChangePassword.module.scss';
import { Capacitor } from '@capacitor/core';

// Espera entre el cambio de clave caducada y la petición del token con la clave nueva
const TOKEN_AFTER_CHANGE_DELAY_MS = 3000;

interface ChangePasswordProps {
  /** La clave actual no cumple los requisitos: no se puede salir sin cambiarla */
  forced?: boolean;
  /** Login rechazado con USR0032: se cambia la clave sin sesión y después se entra con la nueva */
  expired?: boolean;
}

export default function ChangePassword({ forced = false, expired = false }: ChangePasswordProps) {
  const history = useHistory();
  const dispatch = useDispatch();
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  // Evita un segundo envío mientras la llamada está en curso
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Aviso de clave caducada; el del cambio obligatorio está en ForcedPasswordChangeRoutes
  const [expiredNoticeAccepted, setExpiredNoticeAccepted] = useState(false);
  const expiredCredentials = useSelector(selectExpiredCredentials);
  // Solo hay clave anterior en el cambio obligatorio y en el de clave caducada
  const forcedPreviousPassword = useSelector(selectPreviousPassword);
  const previousPassword = expired ? expiredCredentials?.password : forcedPreviousPassword;
  const {
    newPassword,
    setNewPassword,
    repeatedPassword,
    setRepeatedPassword,
    isValid,
    errorMessage,
    newPasswordStatus,
    repeatedPasswordStatus,
    reset: fieldsReset,
  } = useNewPassword(previousPassword);
  const repeatInputRef = useRef<PinInputsHandle>(null);

  const { presentModal: presentSuccessModal } = useModal(SuccessModal);
  const { presentModal: presentSetupBiometryModal } = useModal(SetupBiometryModal);

  // La biometría guardada tiene la clave anterior: se borra y, si el dispositivo
  // la soporta, se ofrece volver a guardarla con la nueva
  const renewBiometry = async (username: string | null, password: string) => {
    if (storageService.getBiometrySaved() === null) return;
    await clearBiometricCredentials().catch(storageService.clearBiometrySaved);

    const biometryAvailable = await typeOfBiometryAvailable();
    if (!username || biometryAvailable === undefined) return;

    await new Promise<void>((resolve) => {
      presentSetupBiometryModal<SetupBiometryModalProps>({
        biometryType: biometryAvailable,
        onDismiss: async (configure) => {
          // Si el guardado falla hay que seguir igualmente: quien espera esta promesa navega
          try {
            if (configure) {
              await setBiometricCredentials(username, password);
            }
          } finally {
            resolve();
          }
        },
      });
    });
  };

  // Sin las credenciales del login (p. ej. al recargar) no se puede cambiar: vuelve al login
  useEffect(() => {
    if (expired && !expiredCredentials) {
      history.replace(ROUTES_PATH.LOGIN);
    }
  }, [expired, expiredCredentials]);

  // Mismos pasos que el login con clave (LoginKeys), con la clave recién cambiada
  const startSession = async (username: string, password: string) => {
    const tokenResponse = await postToken({
      grant_type: 'password',
      username,
      password,
      realm: 'app_merchant',
    });
    Analytics.registerAnalyticsEvent({ [ClientTaxonomyEvents.LOGIN]: {} });
    InsightService.logTrace('Login', [{ Login: tokenResponse.result?.info }]);
    const { access_token, refresh_token } = tokenResponse.data;
    const expirationDate = new Date(
      new Date().setHours(new Date().getHours() + 2),
    ).toString();
    localStorage.setItem('username', username.toUpperCase());
    sessionStorage.setItem('expirationDate', expirationDate);
    sessionStorage.setItem('accessToken', access_token);
    sessionStorage.setItem('refreshToken', refresh_token);
    if (ENVIRONMENT.ENV && access_token) {
      loginTrack(username.toUpperCase());
    }
    postLeads()
      .then((leadsResponse) => {
        sessionStorage.setItem('leadToken', leadsResponse?.data.token);
        const leadsExpirationDate = new Date();
        leadsExpirationDate.setSeconds(
          leadsExpirationDate.getSeconds() + leadsResponse?.data.expires_in,
        );
        sessionStorage.setItem('expiresLeads', leadsExpirationDate.toUTCString());
      })
      .catch(() => dispatch(addError('')));
  };

  /* Devuelve false si no se ha podido cambiar. Si la clave se cambia pero falla el token,
  se avisa de que la clave sí está cambiada y se manda al login para que entre con la
  nueva: reintentar el cambio con la clave anterior ya no funcionaría */
  const changeExpiredPassword = async ({ username, password }: ExpiredCredentials) => {
    await postPasswordChange({
      username,
      password,
      new_password: newPassword,
      realm: 'app_merchant',
    });
    InsightService.logTrace('Expired Password POST', [{ User: username.toUpperCase() }]);
    // Margen para que el back tenga la clave nueva antes de pedir el token.
    // El botón sigue en loading porque isSubmitting no se libera hasta el finally
    await new Promise((resolve) => { setTimeout(resolve, TOKEN_AFTER_CHANGE_DELAY_MS); });
    try {
      await startSession(username, newPassword);
      return true;
    } catch (err) {
      // En un 401 el interceptor ya ha navegado al login y cerrado los modales abiertos,
      // así que el aviso se presenta después. Las credenciales se limpian al cerrarlo:
      // antes, el useEffect de arriba navegaría al login y el aviso podría perderse
      presentSuccessModal({
        title: 'Hemos actualizado tu clave',
        message: 'No hemos podido iniciar sesión. Entra con tu nueva clave.',
        txtButton: 'Entendido',
        onDismiss: () => {
          dispatch(clearExpiredCredentials());
          history.replace(ROUTES_PATH.LOGIN);
        },
      });
      return false;
    }
  };

  const changePasswordUser = async () => {
    // Además del botón deshabilitado: nunca se envía una clave que incumpla las reglas
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (expired) {
        if (!expiredCredentials || !(await changeExpiredPassword(expiredCredentials))) return;
      } else {
        await changePassword(newPassword);
        InsightService.logTrace('Reset Password PATCH', [
          { User: localStorage.getItem('username') },
        ]);
      }
      const changedPassword = newPassword;
      // En la clave caducada, la biometría se guarda con el usuario tal como lo escribió
      const biometryUsername = expired
        ? expiredCredentials!.username
        : localStorage.getItem('username');
      presentSuccessModal({
        message: 'Hemos actualizado tu clave',
        onDismiss: async () => {
          // Un fallo con la biometría no puede dejar al usuario sin salir de la pantalla
          await renewBiometry(biometryUsername, changedPassword).catch(() => {});
          if (forced || expired) {
            storageService.clearMustChangePassword();
            dispatch(clearExpiredCredentials());
            // El router vuelve al layout normal con menú y todas las rutas
            dispatch(checkIsLogged());
          }
          // Directo al dashboard: /login no existe en las rutas con sesión y la redirección
          // comodín del IonRouterOutlet puede dejar la navegación bloqueada
          history.push(ROUTES_PATH.OPERATION_DASHBOARD);
        },
      });
      // Se deja todo en estado inicial
      fieldsReset();
      Analytics.registerAnalyticsEvent({ [ClientTaxonomyEvents.PASSWORD_CHANGE]: {} });
    } catch (err) {
      // Falla el cambio de clave: la anterior sigue siendo la válida y puede volver a intentarlo
      presentSuccessModal({
        title: '',
        message: 'No hemos podido actualizar tu clave. Inténtalo de nuevo.',
        srcImage: PasswordErrorImg,
        txtButton: 'Volver',
        onDismiss: () => {},
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isMobileBrowser = !Capacitor.isNativePlatform() && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <IonPage className={styles.pageChangePassword}>
      <div className={styles.changePasswordLoginHeader}>
        <div className={styles.logoContainer}>
          <IonImg src={LogoPepper} className={styles.logoPepper} />
        </div>
      </div>
      <div
        className={classNames(styles.loginScreen, styles.loginMobile)}
      >
        <IonImg src={LogoPepper} className={styles.loginScreenImg} />
        <IonLabel className={styles.loginScreenLabel}>Comercios</IonLabel>
      </div>
      <div className={styles.loginPageContent}>
        {!isPlatform('android') && !isPlatform('ios') && (
          <div className={classNames(styles.loginScreen, styles.loginDesktop, styles.loginAppImg)}>
            <IonImg src={AppImg} className={styles.loginScreenImg} />
            <LoginAd />
          </div>
        )}
        <div className={styles.changePassword}>
          <LoginScreen />
        </div>
        <div className={styles.passwords}>
          <IonCard className={classNames(styles.cardPassword, isMobileBrowser && styles.cardPasswordMobileBrowser)}>
            <IonCardHeader className={styles.cardPasswordHeader}>
              <IonCardTitle className={styles.cardPasswordHeaderTitle}>
                Cambiar clave de acceso
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent className={styles.cardPasswordContent}>
              <div>
                <IonLabel className={styles.contentNewPassword}>
                  Introduce tu nueva contraseña
                </IonLabel>
                <PinInputs
                  id="new-password-input"
                  value={newPassword}
                  onChange={setNewPassword}
                  onComplete={() => repeatInputRef.current?.focus()}
                  status={newPasswordStatus}
                  autoFocus
                />
              </div>
              <div>
                <IonLabel className={styles.contentNewPassword}>
                  Repite la contraseña
                </IonLabel>
                <PinInputs
                  ref={repeatInputRef}
                  id="repeat-password-input"
                  value={repeatedPassword}
                  onChange={setRepeatedPassword}
                  status={repeatedPasswordStatus}
                />
              </div>
              <NewPasswordHint errorMessage={errorMessage} />
              <div className={classNames(styles.buttonsChangePassword, isMobileBrowser && styles.buttonsChangePasswordMobile)}>
                <IonButton
                  className={styles.buttonChangePasswordSend}
                  onClick={changePasswordUser}
                  disabled={!isValid || isSubmitting}
                  aria-busy={isSubmitting}
                  aria-label={isSubmitting ? 'Enviando' : undefined}
                >
                  {isSubmitting ? <IonSpinner name="crescent" className={styles.sendSpinner} aria-hidden="true" /> : 'Enviar'}
                </IonButton>
                {!forced && !expired && (
                  <IonButton
                    className={styles.buttonChangePasswordDontSend}
                    onClick={() => {
                      screenWidth > 991
                        ? history.push(ROUTES_PATH.PROFILE)
                        : history.push(ROUTES_PATH.CONFIGURATION);
                      // Se deja todo en estado inicial
                      fieldsReset();
                    }}
                  >
                    No quiero cambiar mi contraseña
                  </IonButton>
                )}
              </div>
            </IonCardContent>
          </IonCard>
        </div>
      </div>
      {expired && (
        <NotificationModal
          showModal={!expiredNoticeAccepted}
          closeModal={() => setExpiredNoticeAccepted(true)}
          canDismiss={false}
          title="Actualiza tu contraseña."
          message="Para proteger tu cuenta, es necesario actualizar tu contraseña ya que la actual no cumple con los requisitos de seguridad"
        />
      )}
    </IonPage>
  );
}
